import { useMemo, useState } from "react";
import {
  Loader2,
  MessageCircleQuestion,
  Search,
  ShieldAlert,
} from "lucide-react";
import { DevAgentChatMessageContent } from "../../chat/DevAgentChatMessageContent";
import { useDevAgentStore } from "@/stores/devAgentStore";
import { useDebounce } from "@/utils/useDebounce";
import { useSearchMetadataItems } from "@/hooks/metadata_v1";
import { cn } from "@/lib/utils";

type ClarificationType =
  | "tool_confirmation"
  | "plan_approval"
  | "slot_filling"
  | "ask_user";

function detectClarificationType(data: any): ClarificationType {
  if (data.tool_confirmation || data.option_type === "tool_confirmation")
    return "tool_confirmation";
  if (data.plan_approval || data.option_type === "plan_approval")
    return "plan_approval";
  if (data.slot_filling || data.option_type === "slot_filling")
    return "slot_filling";
  return "ask_user";
}

const CLARIFICATION_STYLES: Record<
  ClarificationType,
  {
    border: string;
    bg: string;
    title: string;
    titleColor: string;
    primaryBtn: string;
    icon: typeof ShieldAlert;
  }
> = {
  tool_confirmation: {
    border: "border-amber-200 dark:border-amber-800",
    bg: "bg-amber-50 dark:bg-amber-950/30",
    title: "Confirmation Required",
    titleColor: "text-amber-700 dark:text-amber-300",
    primaryBtn: "bg-amber-600 text-white hover:bg-amber-700",
    icon: ShieldAlert,
  },
  plan_approval: {
    border: "border-purple-200 dark:border-purple-800",
    bg: "bg-purple-50 dark:bg-purple-950/30",
    title: "Plan Approval",
    titleColor: "text-purple-700 dark:text-purple-300",
    primaryBtn: "bg-purple-600 text-white hover:bg-purple-700",
    icon: ShieldAlert,
  },
  slot_filling: {
    border: "border-blue-200 dark:border-blue-800",
    bg: "bg-blue-50 dark:bg-blue-950/30",
    title: "Additional Info Needed",
    titleColor: "text-blue-700 dark:text-blue-300",
    primaryBtn: "bg-blue-600 text-white hover:bg-blue-700",
    icon: Search,
  },
  ask_user: {
    border: "border-blue-200 dark:border-blue-800",
    bg: "bg-blue-50 dark:bg-blue-950/30",
    title: "Your Input Needed",
    titleColor: "text-blue-700 dark:text-blue-300",
    primaryBtn: "bg-blue-600 text-white hover:bg-blue-700",
    icon: MessageCircleQuestion,
  },
};

export function ClarificationCard({
  data,
  itemId,
  onSendMessage,
  disabled,
}: {
  data: any;
  itemId: string;
  /** Second arg `displayText` lets the caller show a short label in chat
   *  while sending a longer prompt to the backend (handleSend supports it). */
  onSendMessage?: (msg: string, displayText?: string) => void;
  disabled?: boolean;
}) {
  const [inputValue, setInputValue] = useState("");
  const [dismissed, setDismissed] = useState(false);
  const debouncedSearch = useDebounce(inputValue, 300);
  const { data: searchResults = [], isLoading: isSearching } =
    useSearchMetadataItems(debouncedSearch, {
      enabled:
        (data.search_enabled ?? true) && debouncedSearch.trim().length >= 2,
    });
  const searchItems = useMemo(() => searchResults.slice(0, 8), [searchResults]);
  const removeFeedItem = useDevAgentStore((state) => state.removeFeedItem);
  const setRunStatus = useDevAgentStore((state) => state.setRunStatus);

  const clarType = detectClarificationType(data);
  const style = CLARIFICATION_STYLES[clarType];
  const Icon = style.icon;

  // Each option is either a string (label === query === display) or
  // { label, query, display? } — `label` is the button text, `query` is the
  // full prompt sent to the backend, `display` (optional) is what shows in
  // chat as the user message (defaults to `label`).
  type OptionShape = { label: string; query: string; display: string };
  const optionPairs = useMemo<OptionShape[]>(
    () =>
      (data.suggestions || data.options || [])
        .map((o: any): OptionShape | null => {
          if (typeof o === "string") return { label: o, query: o, display: o };
          if (o?.label) {
            const label = String(o.label);
            return {
              label,
              query: String(o.query ?? label),
              display: String(o.display ?? label),
            };
          }
          return null;
        })
        .filter((o: OptionShape | null): o is OptionShape => o !== null),
    [data.suggestions, data.options],
  );

  if (dismissed) return null;

  const handleSend = (value: string, display?: string) => {
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    // Prepend original query so the agent knows what the user was asking about
    const originalQuery = data.original_query || "";
    const enriched = originalQuery
      ? `${trimmed} — regarding: ${originalQuery}`
      : trimmed;
    onSendMessage?.(enriched, display || trimmed);
    setInputValue("");
    setDismissed(true);
    removeFeedItem(itemId);
  };

  return (
    <div className="py-1.5">
      <div
        className={cn(
          "p-3 rounded-xl animate-in fade-in border max-w-[85%]",
          style.border,
          style.bg,
        )}
      >
        <p
          className={cn(
            "text-xs font-bold mb-2 uppercase tracking-tight flex items-center gap-1.5",
            style.titleColor,
          )}
        >
          <Icon className="h-3.5 w-3.5" />
          {style.title}
        </p>
        <div className="text-sm mb-2 select-text overflow-x-auto">
          <DevAgentChatMessageContent content={data.message} />
        </div>

        {optionPairs.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2">
            {optionPairs.map((opt, idx: number) => (
              <button
                key={idx}
                type="button"
                onClick={() => handleSend(opt.query, opt.display)}
                disabled={disabled}
                className={cn(
                  "px-3 py-1.5 text-xs font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
                  idx === 0
                    ? style.primaryBtn
                    : "border border-border bg-background hover:bg-muted",
                )}
              >
                {opt.label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => {
                setDismissed(true);
                removeFeedItem(itemId);
                setRunStatus("idle");
              }}
              className="px-3 py-1.5 text-xs font-medium rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              Cancel
            </button>
          </div>
        )}

        <div className="relative mt-2">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && inputValue.trim())
                    handleSend(inputValue);
                }}
                placeholder={
                  data.search_enabled
                    ? "Search your org or type to respond..."
                    : "Type your answer..."
                }
                disabled={disabled}
                className="w-full px-3 py-1.5 text-xs rounded-md border border-border bg-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50"
              />
              {isSearching && (
                <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-3 w-3 animate-spin text-muted-foreground" />
              )}
            </div>
            <button
              type="button"
              onClick={() => handleSend(inputValue)}
              disabled={disabled || !inputValue.trim()}
              className={cn(
                "px-3 py-1.5 text-xs font-medium rounded-md text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
                style.primaryBtn,
              )}
            >
              Send
            </button>
          </div>

          {searchItems.length > 0 && inputValue.trim().length >= 2 && (
            <div className="absolute z-10 left-0 right-12 mt-1 bg-popover border border-border rounded-md shadow-lg overflow-hidden">
              {searchItems.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => {
                    handleSend(item.fullName);
                    setInputValue("");
                  }}
                  className="w-full px-3 py-1.5 flex items-center gap-2 text-left text-xs hover:bg-muted/50 transition-colors"
                >
                  <span className="truncate flex-1">{item.fullName}</span>
                  <span className="text-[10px] px-1.5 py-0.5 bg-muted rounded text-muted-foreground shrink-0">
                    {item.metadataType}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
